import { getToken } from './csrf.js';

/**
 * Base URL for the node backend. Configured per environment:
 * .env.development → http://localhost:8000, .env.production → the live API.
 */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? 'https://api.autodealerug.com';

/**
 * The one way to call the backend. Injects credentials and the CSRF token
 * (which the window.fetch monkey-patch in main.jsx only did for the prod
 * hostname — so this also fixes CSRF against a local backend), JSON-encodes
 * `body`, and announces expired sessions.
 *
 * Usage: apiFetch('/getinventory', { body: { DealershipID } })
 * Method defaults to POST since that is the API's dominant verb.
 */
export async function apiFetch(path, { method = 'POST', body, headers = {}, ...rest } = {}) {
    const token = getToken();
    const res = await fetch(`${API_BASE_URL}${path}`, {
        method,
        credentials: 'include',
        headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'X-CSRF-Token': token } : {}),
            ...headers,
        },
        body: body !== undefined ? JSON.stringify(body) : undefined,
        ...rest,
    });
    //401s mean an expired session — except on the endpoints where they mean
    //bad credentials (login) or where the caller handles the logout itself
    //(getuserdata, the token-issuing endpoint)
    if (res.status === 401 && !path.includes('/getuserdata') && !path.includes('/login')) {
        window.dispatchEvent(new Event('session-expired'));
    }
    //402 from the suspension gate in app.js. Without this every screen simply
    //fails and the app looks broken rather than cut off — and someone who
    //cannot tell those apart calls support instead of paying, which defeats
    //the point of automating collection.
    //
    //Read from a clone so the caller still gets an unconsumed response. The
    //server's own message is carried through rather than duplicated here: it
    //is the single place the wording lives, so changing the policy (suspend
    //vs. drop to a free tier) needs no frontend deploy.
    //
    //Never fires for /billing, which stays deliberately reachable while
    //suspended so the tenant can see and settle what they owe.
    if (res.status === 402 && !path.startsWith('/billing')) {
        res.clone().json().then((body) => {
            if (body?.suspended) {
                window.dispatchEvent(new CustomEvent('account-suspended', {
                    detail: { message: body.message },
                }));
            }
        }).catch(() => { /* not JSON — leave it to the caller */ });
    }
    return res;
}
